import { useRef, useState } from "react";
import { useAppDispatch } from "../../redux/hooks";
import Lottie from "lottie-react";
import imageAnimation from "../../animations/image.json";
import { IoIosCloseCircle } from "react-icons/io";
import { toast } from "react-toastify";
import loadingAnimation from "../../animations/loading.json";
import buttonLoadingAnimation from "../../animations/buttonLoading.json";
import type { CreateBrandFormTypes } from "../../types/brand/brand.form";
import { createBrand } from "../../redux/slices/brandSlice";
import { getErrorMessage } from "../../utils/errorHandler";
import { validateForm } from "../../utils/validateForm";
import { useFileUpload } from "../../hooks/useFileUpload";
import { API } from "../../configs/endpoints";
import type { Image } from "../../types/common/image";
type Payload = {
  onBack: () => void;
};

export const CreateBrand = ({ onBack }: Payload) => {
  const dispatch = useAppDispatch();

  const [brandData, setBrandData] = useState<CreateBrandFormTypes>({
    name: "",
    logo: null,
    isActive: true,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setBrandData((prev) => ({
      ...prev,
      [name]: name === "isActive" ? value === "true" : value,
    }));
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading: isChooseImage } = useFileUpload<{
    logo: Image;
  }>({
    endpoint: API.brand.upload,
    kind: "image",
  });
  const handleChooseImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    const result = await upload(file);
    e.target.value = "";
    if (result.ok && result.data?.logo) {
      setBrandData((prev) => ({
        ...prev,
        logo: {
          url: result.data!.logo.url,
          public_id: result.data!.logo.public_id,
          status: "temple",
        },
      }));
    }
  };

  const [loading, setLoading] = useState<boolean>(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = validateForm([
      { value: brandData.name, label: "Tên thương hiệu" },
      { value: brandData.logo, label: "logo thương hiệu", type: "image" },
    ]);
    if (!valid) return;
    setLoading(true);
    try {
      await dispatch(createBrand(brandData)).unwrap();
      toast.success("Tạo thương hiệu sản phẩm thành công");
      onBack();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 bg-white rounded-lg w-full">
      <h2 className="text-2xl font-bold text-gray-900">Thêm thương hiệu</h2>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex lg:items-center flex-col lg:flex-row gap-2">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Tên thương hiệu
            </label>
            <input
              required
              type="text"
              name="name"
              value={brandData.name}
              onChange={handleChange}
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
              placeholder="Nhập tên thương hiệu"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm text-gray-700 font-semibold">
              Trạng thái
            </label>
            <select
              name="isActive"
              value={String(brandData.isActive)}
              onChange={handleChange}
              className="p-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors duration-300 cursor-pointer"
            >
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Chọn ảnh thương hiệu
          </label>
          <div className="w-25 h-25">
            <input
              ref={inputRef}
              onChange={handleChooseImage}
              type="file"
              className="hidden"
            />
            {brandData.logo ? (
              <div className="w-full h-full relative border border-gray-200 rounded-xl">
                <img
                  src={brandData.logo.url}
                  alt="Ảnh danh mục"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() =>
                    setBrandData((prev) => ({
                      ...prev,
                      logo: null,
                    }))
                  }
                  className="absolute -top-2 -right-2 text-xl text-red-500 cursor-pointer hover:text-red-600"
                >
                  <IoIosCloseCircle />
                </button>
              </div>
            ) : (
              <div className="w-full h-full">
                {isChooseImage ? (
                  <Lottie animationData={loadingAnimation} loop={true} />
                ) : (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="w-full h-full border border-gray-200 rounded-xl cursor-pointer"
                  >
                    <Lottie animationData={imageAnimation} loop={true} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="">
            <button
              type="button"
              onClick={onBack}
              className="p-2 text-white rounded-ld bg-blue-500 text-sm rounded-lg cursor-pointer hover:bg-blue-600 transition-colors duration-300"
            >
              Quay lại
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setBrandData({
                  name: "",
                  logo: null,
                  isActive: true,
                });
              }}
              className="p-2 rounded-lg text-sm text-white bg-red-500 hover:bg-red-600 transition-colors duration-300 cursor-pointer"
            >
              Hủy
            </button>
            {loading ? (
              <Lottie
                animationData={buttonLoadingAnimation}
                loop={true}
                className="h-10 w-20"
              />
            ) : (
              <button
                type="submit"
                className="p-2 rounded-lg text-sm text-white bg-green-500 hover:bg-green-600 transition-colors duration-300 cursor-pointer"
              >
                Thêm Thương hiệu
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
